import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ShieldAlert,
  ShieldCheck,
  FileText,
  FileWarning,
  ArrowRight,
  Upload,
  XCircle,
  Info,
  RefreshCw,
  ExternalLink,
  Lock,
} from 'lucide-react';
import {
  CompanionProfileLifecycleStatus,
  validateCompanionDocuments,
  CompanionDocumentEntry,
  DOCUMENT_REQUIREMENTS_REGISTRY,
  SupportedDocumentType,
  DocumentValidationResult,
  normalizeDocumentType,
} from '../../utils/documentValidation';
import {
  subscribeToCompanionDocuments,
  subscribeToCompanionVerification,
  createDefaultDocumentRecord,
} from '../../lib/verificationService';
import { UserProfile } from '../../types';
import { CompanionDocumentRecord } from '../../types/verification.types';
import { ProfileStatusBadge } from './ProfileStatusBadge';
import { mapFirestoreStateToLifecycleStatus } from '../../utils/companionStatusManager';

export interface CompanionProfileStatusIndicatorProps {
  companionId?: string;
  userProfile?: UserProfile | null;
  profileStatus?: CompanionProfileLifecycleStatus;
  documents?: Record<string, CompanionDocumentEntry | CompanionDocumentRecord | any>;
  onNavigateToVerification?: () => void;
  compact?: boolean;
  className?: string;
}

/**
 * Visual Status Indicator for Companion Dashboard
 * Displays the current 'profileStatus' and lists specific missing documents
 * when the status is 'documents_incomplete', using the centralized validation utility.
 */
export const CompanionProfileStatusIndicator: React.FC<CompanionProfileStatusIndicatorProps> = ({
  companionId: propCompanionId,
  userProfile,
  profileStatus: propProfileStatus,
  documents: propDocuments,
  onNavigateToVerification,
  compact = false,
  className = '',
}) => {
  // Resolve Companion ID
  const effectiveCompanionId = useMemo(() => {
    if (propCompanionId) return propCompanionId;
    if ((userProfile as any)?.uid) return (userProfile as any).uid;
    if (userProfile?.email) return userProfile.email.replace(/[^a-zA-Z0-9]/g, '_');
    return 'CP1024';
  }, [propCompanionId, userProfile]);

  // Real-time document subscription state
  const [liveDocs, setLiveDocs] = useState<Record<string, any>>(() => {
    if (propDocuments) return propDocuments;
    // Fallback to local storage cache if available
    try {
      const cached = localStorage.getItem(`pairup_verification_docs_${effectiveCompanionId}`);
      if (cached) return JSON.parse(cached);
    } catch {}
    return {};
  });

  // Admin verdict / verification profile state
  const [liveVerificationProfile, setLiveVerificationProfile] = useState<any>(null);

  // Subscribe to Firestore/Local documents when effectiveCompanionId changes
  useEffect(() => {
    if (propDocuments) {
      setLiveDocs(propDocuments);
      return;
    }

    if (!effectiveCompanionId) return;

    const unsubDocs = subscribeToCompanionDocuments(effectiveCompanionId as any, (docs) => {
      setLiveDocs(docs);
    });

    const unsubProfile = subscribeToCompanionVerification(effectiveCompanionId, (p) => {
      setLiveVerificationProfile(p);
    });

    return () => {
      unsubDocs();
      unsubProfile();
    };
  }, [effectiveCompanionId, propDocuments]);

  // Derive unified document mapping incorporating userProfile fallbacks
  const documentsMap = useMemo<Record<string, CompanionDocumentEntry>>(() => {
    const map: Record<string, CompanionDocumentEntry> = { ...liveDocs };

    // Check userProfile for driving license details if available
    const dlDetails = userProfile?.drivingLicenceDetails || userProfile?.companionProfile?.drivingLicence;
    if (dlDetails) {
      const dlKey = 'driving_license';
      const existingDl = map[dlKey] || {};
      map[dlKey] = {
        ...existingDl,
        idNumber: dlDetails.licenceNumber || (existingDl as any).idNumber,
        front: dlDetails.frontUrl || existingDl.front,
        back: dlDetails.backUrl || existingDl.back,
      };
    }

    // Check userProfile for primary ID document fallback if available
    const compPrimaryDoc = userProfile?.companionProfile;
    if (compPrimaryDoc?.idType && compPrimaryDoc?.idDocumentUrl) {
      const normalizedType = normalizeDocumentType(compPrimaryDoc.idType);
      const existing = map[normalizedType] || {};
      map[normalizedType] = {
        ...existing,
        idNumber: compPrimaryDoc.idNumber || (existing as any).idNumber,
        front: existing.front || compPrimaryDoc.idDocumentUrl,
        back: existing.back || (compPrimaryDoc as any).backUrl || (compPrimaryDoc as any).idBackDocumentUrl,
      };
    }

    return map;
  }, [liveDocs, userProfile]);

  // Determine services and driver category
  const isDriver = useMemo(() => {
    const services =
      userProfile?.companionProfile?.services ||
      (userProfile?.services ? userProfile.services.map((s) => s.name) : []);
    return (
      Boolean(userProfile?.isDriverApproved) ||
      services.some((s) => typeof s === 'string' && s.toLowerCase().includes('driver'))
    );
  }, [userProfile]);

  const selectedPrimaryDocType = useMemo(() => {
    return (
      userProfile?.companionProfile?.idType ||
      liveVerificationProfile?.primaryDocType ||
      'aadhaar'
    );
  }, [userProfile, liveVerificationProfile]);

  // Run centralized validation utility
  const validationResult: DocumentValidationResult = useMemo(() => {
    // Current admin verdict if profile exists
    const currentAdminVerdict =
      liveVerificationProfile?.verificationStatus === 'verified' || userProfile?.isIdVerified
        ? 'verified'
        : liveVerificationProfile?.verificationStatus === 'rejected'
        ? 'rejected'
        : null;

    const isProfileInfoComplete = Boolean(
      userProfile?.displayName || userProfile?.name || userProfile?.companionProfile?.displayName
    );

    const verificationStarted =
      liveVerificationProfile?.verificationStatus === 'under_review' ||
      liveVerificationProfile?.verificationStatus === 'submitted';

    return validateCompanionDocuments(documentsMap, {
      selectedPrimaryDocType,
      isDriverService: isDriver,
      isProfileInfoComplete,
      verificationStarted,
      currentAdminVerdict,
    });
  }, [documentsMap, selectedPrimaryDocType, isDriver, userProfile, liveVerificationProfile]);

  // Authoritative current profileStatus: prop override or calculated from validation utility
  const currentProfileStatus: CompanionProfileLifecycleStatus = useMemo(() => {
    if (propProfileStatus) {
      return propProfileStatus;
    }
    // If explicitly stored on userProfile as lifecycle status
    if (
      userProfile?.profileStatus &&
      ['draft', 'documents_incomplete', 'documents_uploaded', 'under_review', 'verified', 'rejected', 'suspended'].includes(
        userProfile.profileStatus as string
      )
    ) {
      return userProfile.profileStatus as CompanionProfileLifecycleStatus;
    }
    return mapFirestoreStateToLifecycleStatus({
      documents: documentsMap,
      selectedPrimaryDocType,
      selectedServices: userProfile?.companionProfile?.services || [],
      isDriverService: isDriver,
      adminVerdict:
        liveVerificationProfile?.verificationStatus === 'verified' || userProfile?.isIdVerified
          ? 'verified'
          : liveVerificationProfile?.verificationStatus === 'rejected'
          ? 'rejected'
          : null,
      verificationStatus: liveVerificationProfile?.verificationStatus,
      identityStatus: liveVerificationProfile?.identityStatus,
      rejectionReason: liveVerificationProfile?.rejectionReason,
      isSuspended: Boolean((userProfile as any)?.accountStatus === 'Suspended' || liveVerificationProfile?.isSuspended),
    });
  }, [
    propProfileStatus,
    userProfile?.profileStatus,
    documentsMap,
    selectedPrimaryDocType,
    userProfile,
    isDriver,
    liveVerificationProfile,
  ]);

  // Status visual configurations
  const statusConfig = useMemo(() => {
    switch (currentProfileStatus) {
      case 'documents_incomplete':
        return {
          label: 'Documents Incomplete',
          badgeText: 'DOCUMENTS INCOMPLETE',
          badgeBg: 'bg-amber-950/90 text-amber-300 border-amber-600/70',
          dotColor: 'bg-amber-400 animate-pulse',
          cardBg: 'bg-gradient-to-br from-amber-950/70 via-slate-900 to-rose-950/60 border-amber-500/50',
          textColor: 'text-amber-200',
          accentColor: 'text-amber-400',
          Icon: AlertTriangle,
          title: 'Action Required: Incomplete Mandatory Documents',
          description:
            'All required government ID sides and pages must be uploaded before your companion profile can be submitted for verification.',
        };
      case 'documents_uploaded':
        return {
          label: 'Documents Uploaded',
          badgeText: 'DOCUMENTS UPLOADED',
          badgeBg: 'bg-blue-950/90 text-blue-300 border-blue-600/70',
          dotColor: 'bg-blue-400',
          cardBg: 'bg-gradient-to-br from-blue-950/70 via-slate-900 to-slate-900 border-blue-500/50',
          textColor: 'text-blue-200',
          accentColor: 'text-blue-400',
          Icon: Upload,
          title: 'All Mandatory Documents Uploaded',
          description: 'Your document pages are complete and ready for final review submission.',
        };
      case 'under_review':
        return {
          label: 'Under Review',
          badgeText: 'UNDER REVIEW',
          badgeBg: 'bg-purple-950/90 text-purple-300 border-purple-600/70',
          dotColor: 'bg-purple-400 animate-pulse',
          cardBg: 'bg-gradient-to-br from-purple-950/70 via-slate-900 to-slate-900 border-purple-500/50',
          textColor: 'text-purple-200',
          accentColor: 'text-purple-400',
          Icon: Clock,
          title: 'Verification In Progress',
          description: 'PairUpToday Trust & Safety team is reviewing your uploaded documents.',
        };
      case 'verified':
        return {
          label: 'Verified',
          badgeText: 'VERIFIED ✓',
          badgeBg: 'bg-emerald-950/90 text-emerald-300 border-emerald-600/70',
          dotColor: 'bg-emerald-400',
          cardBg: 'bg-gradient-to-br from-emerald-950/70 via-slate-900 to-slate-900 border-emerald-500/50',
          textColor: 'text-emerald-200',
          accentColor: 'text-emerald-400',
          Icon: ShieldCheck,
          title: 'Profile Fully Verified',
          description: 'All mandatory documents verified. Your companion profile is active and bookable.',
        };
      case 'rejected':
        return {
          label: 'Rejected',
          badgeText: 'RE-UPLOAD REQUIRED',
          badgeBg: 'bg-rose-950/90 text-rose-300 border-rose-600/70',
          dotColor: 'bg-rose-400 animate-ping',
          cardBg: 'bg-gradient-to-br from-rose-950/70 via-slate-900 to-slate-900 border-rose-500/50',
          textColor: 'text-rose-200',
          accentColor: 'text-rose-400',
          Icon: XCircle,
          title: 'Verification Requires Revision',
          description: 'One or more submitted document sides were rejected by admin. Please review and re-upload.',
        };
      case 'suspended':
        return {
          label: 'Suspended',
          badgeText: 'SUSPENDED',
          badgeBg: 'bg-red-950/90 text-red-300 border-red-700',
          dotColor: 'bg-red-500',
          cardBg: 'bg-slate-900 border-red-800',
          textColor: 'text-red-200',
          accentColor: 'text-red-400',
          Icon: ShieldAlert,
          title: 'Companion Account Suspended',
          description: 'This profile has been temporarily suspended. Please contact platform support.',
        };
      case 'draft':
      default:
        return {
          label: 'Draft',
          badgeText: 'DRAFT',
          badgeBg: 'bg-slate-800/90 text-slate-300 border-slate-600',
          dotColor: 'bg-slate-400',
          cardBg: 'bg-slate-900 border-slate-800',
          textColor: 'text-slate-300',
          accentColor: 'text-slate-400',
          Icon: FileText,
          title: 'Profile Draft Incomplete',
          description: 'Please complete your profile information and upload required identity documents.',
        };
    }
  }, [currentProfileStatus]);

  const StatusIcon = statusConfig.Icon;
  const isDocumentsIncomplete = currentProfileStatus === 'documents_incomplete';

  // Specific missing items from validation utility
  const missingItems = validationResult.missingItems || [];
  const uploadingItems = validationResult.uploadingItems || [];

  // ================= COMPACT BADGE VARIANT =================
  if (compact) {
    return (
      <ProfileStatusBadge
        profileStatus={currentProfileStatus}
        userProfile={userProfile}
        companionId={effectiveCompanionId}
        documents={documentsMap}
        missingItems={missingItems}
        missingCount={missingItems.length}
        onNavigateToVerification={onNavigateToVerification}
        className={className}
      />
    );
  }

  // ================= FULL DASHBOARD INDICATOR CARD =================
  return (
    <div
      data-testid="companion-profile-status-indicator"
      data-profile-status={currentProfileStatus}
      className={`rounded-3xl border p-5 sm:p-6 shadow-xl transition-all relative overflow-hidden ${statusConfig.cardBg} ${className}`}
    >
      {/* Decorative background glow */}
      <div className="absolute top-0 right-0 -mr-12 -mt-12 w-48 h-48 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-2xl pointer-events-none" />

      <div className="relative z-10 space-y-4">
        {/* Header line: Current profileStatus badge + Machine readable label */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 rounded-2xl bg-black/40 border border-white/10 shrink-0">
              <StatusIcon className={`w-5 h-5 ${statusConfig.accentColor}`} />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold">
                  Profile Status
                </span>
                <span className="text-slate-600">•</span>
                <code
                  data-testid="profile-status-raw"
                  className="text-[11px] font-mono font-bold px-1.5 py-0.2 rounded bg-black/40 text-purple-300 border border-purple-800/50"
                >
                  {currentProfileStatus}
                </code>
              </div>

              <h2 className="text-lg font-black text-white tracking-tight flex items-center gap-2 mt-0.5">
                <span>{statusConfig.title}</span>
              </h2>
            </div>
          </div>

          {/* Current profileStatus Pill Badge */}
          <div
            data-testid="companion-profile-status-pill"
            className={`px-3 py-1.5 rounded-full text-xs font-mono font-black border uppercase tracking-wider flex items-center gap-2 shadow-sm ${statusConfig.badgeBg}`}
          >
            <span className={`w-2 h-2 rounded-full ${statusConfig.dotColor}`} />
            <span>{statusConfig.badgeText}</span>
          </div>
        </div>

        {/* Informative text */}
        <p className={`text-xs leading-relaxed max-w-3xl ${statusConfig.textColor}`}>
          {statusConfig.description}
        </p>

        {/* ========================================================= */}
        {/* SPECIFIC MISSING DOCUMENTS LIST (FOR documents_incomplete) */}
        {/* ========================================================= */}
        {isDocumentsIncomplete && (
          <div
            data-testid="missing-documents-container"
            className="mt-3 p-4 rounded-2xl bg-black/50 border border-amber-500/40 space-y-3"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2.5">
              <div className="flex items-center gap-2">
                <FileWarning className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="text-xs font-bold uppercase tracking-wider text-amber-300 font-mono">
                  Mandatory Document Sides Missing ({missingItems.length})
                </span>
              </div>
              <span className="text-[11px] text-slate-400 font-mono">
                Central Validation Utility Rule: All sides strictly enforced
              </span>
            </div>

            {missingItems.length > 0 ? (
              <ul className="space-y-2" data-testid="missing-documents-list">
                {missingItems.map((item, idx) => {
                  const reqMeta = DOCUMENT_REQUIREMENTS_REGISTRY[item.documentId as SupportedDocumentType];
                  const sideText =
                    item.side === 'front'
                      ? 'Front Side'
                      : item.side === 'back'
                      ? 'Back Side'
                      : item.side;

                  return (
                    <li
                      key={`${item.documentId}-${item.side}-${idx}`}
                      data-testid={`missing-document-item-${item.documentId}-${item.side}`}
                      className="p-3 rounded-xl bg-slate-900/90 border border-rose-900/60 hover:border-amber-600/60 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-6 h-6 rounded-lg bg-rose-950 text-rose-400 border border-rose-800 flex items-center justify-center shrink-0 mt-0.5">
                          <XCircle className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-white">
                              {item.documentTitle}
                            </span>
                            <span className="px-2 py-0.2 rounded-full text-[10px] font-mono font-bold bg-rose-950/80 text-rose-300 border border-rose-800 uppercase">
                              {sideText} Missing
                            </span>
                            {reqMeta?.badge && (
                              <span className="text-[10px] text-slate-400 font-mono">
                                ({reqMeta.badge})
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            <span className="text-amber-400/90 font-medium">{item.label}</span>
                            {' • '}
                            <span>{item.reason}</span>
                          </p>
                        </div>
                      </div>

                      {/* Direct upload link / action */}
                      {onNavigateToVerification && (
                        <button
                          type="button"
                          onClick={onNavigateToVerification}
                          className="self-start sm:self-center px-3 py-1.5 rounded-lg bg-pink-950/70 hover:bg-pink-900 text-pink-300 border border-pink-800/80 text-[11px] font-bold font-mono transition-all flex items-center gap-1 cursor-pointer shrink-0"
                        >
                          <Upload className="w-3 h-3" />
                          <span>Upload Now</span>
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-xs text-amber-300 italic p-2">
                Document sides undergoing completeness verification...
              </div>
            )}

            {/* Any items currently uploading */}
            {uploadingItems.length > 0 && (
              <div className="pt-2 border-t border-white/10 space-y-1.5 text-xs">
                <span className="text-[11px] font-mono font-bold text-blue-400 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Currently Uploading ({uploadingItems.length})
                </span>
                <div className="flex flex-wrap gap-2">
                  {uploadingItems.map((u, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 rounded-lg bg-blue-950/60 border border-blue-800/60 text-blue-300 text-[11px] font-mono"
                    >
                      {u.documentTitle} ({u.side.toUpperCase()})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Validation Errors summary */}
            {validationResult.validationErrors.length > 0 && (
              <div className="pt-2 border-t border-white/10 space-y-1 text-[11px] text-amber-300/90">
                <span className="font-bold font-mono text-[10px] uppercase text-amber-400 block">
                  Validation Utility Checklist:
                </span>
                <ul className="list-disc list-inside space-y-0.5 text-slate-300">
                  {validationResult.validationErrors.slice(0, 3).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {validationResult.validationErrors.length > 3 && (
                    <li className="text-slate-400 italic">
                      + {validationResult.validationErrors.length - 3} more side requirement(s)
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Action Button footer */}
        <div className="pt-2 flex flex-wrap items-center justify-between gap-3 border-t border-white/10">
          <div className="text-[11px] font-mono text-slate-400 flex items-center gap-2">
            <span>Authoritative System Rule:</span>
            <span className="text-purple-300 font-semibold">
              Profile activates upon document completion & verification.
            </span>
          </div>

          {onNavigateToVerification && (
            <button
              type="button"
              data-testid="resolve-verification-cta"
              onClick={onNavigateToVerification}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-md ${
                isDocumentsIncomplete
                  ? 'bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white shadow-amber-950/50'
                  : currentProfileStatus === 'verified'
                  ? 'bg-emerald-950 hover:bg-emerald-900 text-emerald-300 border border-emerald-800'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
              }`}
            >
              <span>
                {isDocumentsIncomplete
                  ? 'Upload Missing Documents'
                  : currentProfileStatus === 'verified'
                  ? 'View Verified Documents'
                  : 'Go to Verification Center'}
              </span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
